import React, { useCallback, useRef, useState, Fragment } from "react";
import {
  AnyObject,
  Text,
  TextEditor,
  Flex,
  Item,
  Textarea,
  Button,
  Input,
  KeyName,
} from "@thenounproject/lingo-core";

import ModalBody from "../../../../app/_components/ModalBody";
import ModalHeader from "../../../../app/_components/ModalHeader";
import ModalFooter, { ModalFooterContainer } from "../../../../app/_components/ModalFooter";
import useShowModal from "@redux/actions/useModals";
import useGenerateNoteContent from "@redux/actions/items/useGenerateNoteContent";
import useUpdateItem from "@redux/actions/items/useUpdateItem";
import useKeyboardShortcut from "@hooks/useKeyboardShortcut";

type Props = {
  item: Item;
  buildSaveData: (content: string) => AnyObject;
};

const GenerateTextContentModal: React.FC<Props> = ({ item, buildSaveData }) => {
  const itemId = item.id;
  const [prompt, setPrompt] = useState(""),
    [editingPrompt, setEditingPrompt] = useState<string | null>(null),
    [generatedContent, setGeneratedContent] = useState<string | null>(null),
    [isEditing, setIsEditing] = useState(false),
    editedContentRef = useRef<string | null>(null),
    { dismissModal } = useShowModal(),
    [generateContent, { isProcessing: isGenerating, error: generateError }] =
      useGenerateNoteContent(),
    [updateItem, { isProcessing: isSaving }] = useUpdateItem();

  const hasPreview = generatedContent !== null;

  const apply = useCallback(async () => {
    const content = editedContentRef.current;
    if (!content) return;
    setEditingPrompt(null);
    await updateItem({ itemId, updates: buildSaveData(content) });
    dismissModal();
  }, [updateItem, itemId, buildSaveData, dismissModal]);

  const handleBeginEditing = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handleFinishEditing = useCallback(() => {
    setGeneratedContent(editedContentRef.current);
    setIsEditing(false);
  }, []);

  const makeChanges = useCallback(() => {
    setEditingPrompt("");
  }, []);

  const startOver = useCallback(() => {
    setGeneratedContent(null);
    setIsEditing(false);
    setEditingPrompt(null);
  }, []);

  const endChanges = useCallback(() => {
    setEditingPrompt(null);
  }, []);

  const generate = useCallback(async () => {
    if (hasPreview && !editingPrompt) return endChanges();
    if (!hasPreview && !prompt) return;

    setEditingPrompt(null);
    const res = await generateContent({
      itemId,
      prompt: editingPrompt ?? prompt,
      previousContent: generatedContent,
    });
    if (res.isSuccess && res.response) {
      const content = res.response.result.content;
      setGeneratedContent(content);
      editedContentRef.current = content;
    }
  }, [editingPrompt, endChanges, generateContent, generatedContent, hasPreview, itemId, prompt]);

  const primaryAction = hasPreview && !isGenerating ? apply : generate,
    primaryDisabled = hasPreview && !isGenerating ? isSaving : isGenerating || !prompt.trim();
  useKeyboardShortcut(KeyName.enter, primaryAction, { meta: true, disabled: primaryDisabled });
  useKeyboardShortcut(KeyName.escape, endChanges, {
    disabled: editingPrompt === null,
  });

  function renderEditor() {
    return (
      <Input
        value={editingPrompt}
        placeholder="Describe your changes"
        onSubmit={generate}
        onChange={e => setEditingPrompt(e.target.value)}
        autoFocus>
        <Button
          buttonStyle="primary"
          text="Make changes"
          flexShrink={0}
          ml="s"
          onClick={generate}
        />
        <Button
          buttonStyle="tertiary"
          icon="action.close"
          flexShrink={0}
          ml="s"
          onClick={endChanges}
        />
      </Input>
    );
  }

  function renderFooter() {
    if (editingPrompt !== null) {
      return <ModalFooterContainer>{renderEditor()}</ModalFooterContainer>;
    }
    return (
      <ModalFooter
        secondary={
          hasPreview && editingPrompt === null
            ? {
                text: "Make changes",
                onClick: makeChanges,
                disabled: isGenerating,
              }
            : undefined
        }
        primary={
          hasPreview && !isGenerating
            ? {
                text: isSaving ? "Applying..." : "Apply",
                onClick: apply,
                disabled: isSaving,
              }
            : {
                text: isGenerating ? "Generating..." : "Generate",
                onClick: generate,
                disabled: isGenerating || !prompt.trim(),
              }
        }>
        {hasPreview && (
          <Button
            buttonStyle="tertiary"
            text="Start over"
            onClick={startOver}
            iconBefore="navigation.chevron-left"
            size="small"
            disabled={isGenerating}
          />
        )}
      </ModalFooter>
    );
  }

  return (
    <Fragment>
      <ModalHeader title="Generate Content" />
      <ModalBody>
        <Flex flexDirection="column" gap="8px">
          {hasPreview ? (
            <>
              <Text font="ui.smallBold" mb="s">
                Preview
              </Text>
              <TextEditor
                itemId={itemId}
                content={generatedContent}
                buildSaveData={buildSaveData}
                selected={true}
                canEdit={true}
                isEditing={isEditing}
                onBeginEditing={handleBeginEditing}
                onFinishEditing={handleFinishEditing}
                onClick={handleBeginEditing}
                onContentChange={md => {
                  editedContentRef.current = md;
                }}
                backgroundColor={item.displayProperties?.cardBackgroundColor}
                p="none"
              />
            </>
          ) : (
            <>
              <Text font="ui.smallBold">Prompt</Text>
              <Textarea
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                placeholder="Describe what you'd like to generate..."
                autoFocus
                onSubmit={generate}
              />
            </>
          )}
          {generateError && (
            <Text font="ui.small" color="error">
              {generateError.message}
            </Text>
          )}
        </Flex>
      </ModalBody>
      {renderFooter()}
    </Fragment>
  );
};

export default GenerateTextContentModal;
